import { FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useInputText } from '@/hooks';
import { Validation } from '@/models';
import { useAppSelector } from '@/redux/hooks';
import { CreateRiskProfileRequest } from '@/api';
import { riskscoreProfilesActions } from '@/redux/actions';
import { riskscoreProfilesSelectors } from '@/redux/selectors';
import { Input, InputTextarea } from '@/components/_inputs';
import { Button } from '@/components/Button';
import { AlertBox } from '@/components/AlertBox';
import { InputGroup } from '@/components/InputGroup';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './RiskscoreCreateProfileModal.module.sass';

interface RiskscoreCreateProfileModalProps extends ModalInfoProps {}

const RiskscoreCreateProfileModal: FC<RiskscoreCreateProfileModalProps> = (
  props,
) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: riskProfileData, loading } = useAppSelector(
    riskscoreProfilesSelectors.getRiskscoreProfilesSlice,
  );

  const profileNames = useMemo(() => {
    if (riskProfileData) {
      return riskProfileData?.profiles.map((p) => p.name);
    }
    return [];
  }, [riskProfileData]);

  const NAME_VALIDATIONS: Validation[] = useMemo(() => {
    return [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'cb',
        value: (val) => !profileNames.includes(val),
        message: t('translation.field_is_not_uniq'),
      },
    ];
  }, [t, profileNames]);

  const DESCRIPTION_VALIDATIONS: Validation[] = useMemo(() => {
    return [
      {
        name: 'maxLength',
        value: 100,
        message: t('translation.max_length_error', { length: 100 }),
      },
    ];
  }, [t]);
  const nameField = useInputText('', NAME_VALIDATIONS);
  const descriptionField = useInputText('', DESCRIPTION_VALIDATIONS);

  const modalTitle = t('translation.create_risk_profile_btn');

  const closeHandler = () => {
    onClose();
    nameField.onReset();
    descriptionField.onReset();
  };

  const submitHandler = () => {
    if (!loading) {
      if (nameField.isValid) {
        const payload: CreateRiskProfileRequest = {
          name: nameField.value,
          description: descriptionField.value,
        };

        dispatch(riskscoreProfilesActions.createRiskProfile(payload));
        closeHandler();
      } else if (!nameField.isValid) {
        nameField.onBlur();
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={modalTitle}
      className={styles.modal}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            onClick={submitHandler}
            disabled={loading}
            isLoading={loading}
          >
            {t('translation.create_risk_profile_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            onClick={closeHandler}
            disabled={false}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={closeHandler}
    >
      <div className={styles.form}>
        <InputGroup label={t('translation.profile_inp_name')}>
          <Input
            id="name"
            name="name"
            type="text"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            isError={nameField.isDirty && !nameField.isValid}
            required
          />
          {nameField.isDirty && !nameField.isValid && (
            <AlertBox type="danger" small>
              {{ description: nameField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>
        <InputGroup label={t('translation.profile_inp_description')}>
          <InputTextarea
            id="desription"
            name="desription"
            value={descriptionField.value}
            onChange={descriptionField.onChange}
            onBlur={descriptionField.onBlur}
            isError={descriptionField.isDirty && !descriptionField.isValid}
          />
          {descriptionField.isDirty && !descriptionField.isValid && (
            <AlertBox type="danger" small>
              {{ description: descriptionField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>
      </div>
    </ModalInfo>
  );
};

export default RiskscoreCreateProfileModal;
